/**
 * To be used with the PaymentRequestButtonElement from Stripe.
 */
import { useState, useEffect } from 'react';
import { useStripe } from '@stripe/react-stripe-js';

/**
 * Payment request button for Apple/Google Pay integrations.
 *
 * @param {Object} options The options.
 * @param {string} options.label The label.
 * @param {string} options.currency The currency.
 * @param {number} options.amount The amount to charge.
 * @param {function} options.onStripeToken Callback function on successful charge.
 * @returns {{paymentRequest: StripePaymentRequest}}
 */
const usePaymentRequestButton = ({
  label = 'Waldo Photos',
  currency = 'usd',
  amount,
  onStripeToken,
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  useEffect(() => {
    if (stripe && amount) {
      const paymentRequestResponse = stripe.paymentRequest({
        country: 'US',
        currency,
        total: {
          label,
          amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      (async () => {
        const result = await paymentRequestResponse.canMakePayment();

        if (result) {
          setPaymentRequest(paymentRequestResponse);
        }
      })();
    }
  }, [stripe, amount, label]);

  useEffect(() => {
    if (!paymentRequest) {
      return;
    }

    paymentRequest.on('token', async event => {
      await onStripeToken?.({
        event,
        token: {
          id: event.token.id,
          email: event.payerEmail,
        },
      });
      event.complete('success');
    });
  }, [paymentRequest]);

  return {
    paymentRequest,
  };
};

export default usePaymentRequestButton;
